import { splashScreenCloseRequested } from 'features/breakout-rooms/actions';
import { selectShowBreakoutsSplash } from 'features/breakout-rooms/breakoutRoomsSlice';
import { signalingRoomJoined } from 'features/room/actions';
import { normalizeBreakoutRoomsSaga } from 'features/breakout-rooms/sagas/normalizeBreakoutRoomsSaga';
import { call, fork, put, select } from 'redux-saga/effects';
import { onRoomJoinConfigSaga } from 'features/room/sagas/onRoomJoinConfigSaga';
import { E2EEManager } from 'features/e2ee/E2EEManager';
import { selectE2eeEnabled } from 'features/e2ee/e2eeSlice';
import { selectRoomLastId } from 'features/room/roomSlice';
import { exchangeEncryptedNameSaga } from 'features/e2ee/sagas/exchangeEncryptedNameSaga';
import * as Sentry from '@sentry/react';
import { logger } from 'utils/logger';
import { NormalizedBreakoutRoomsConfig } from 'features/breakout-rooms/types';
import { computeBreakoutUserKeysSaga } from 'features/e2ee/sagas/computeBreakoutUserKeysSaga';
import { hasPermissionsSaga } from 'features/permissions/sagas/hasPermissionsSaga';
import { PermissionTypes } from 'features/permissions/types';
import { RTCClient } from 'utils/webrtc';
import { virtualBackgroundCleared } from 'features/virtual-backgrounds/virtualBackgroundsSlice';
import { board } from 'utils/whiteboard/BoardStateManager';

export function* onRoomJoinedSaga(action: ReturnType<typeof signalingRoomJoined>) {
  const { breakoutRooms, roomId, breakoutRoomManagers } = action.payload;

  const e2eeEnabled: boolean = yield select(selectE2eeEnabled);

  if (breakoutRooms?.rooms.length) {
    const config: NormalizedBreakoutRoomsConfig = yield call(normalizeBreakoutRoomsSaga, {
      rooms: breakoutRooms.rooms,
      unassignedUsers: breakoutRooms.unassignedUsers,
    });

    if (e2eeEnabled) {
      yield call(computeBreakoutUserKeysSaga, config);
    }
  }

  if (e2eeEnabled && breakoutRoomManagers) {
    for (const manager of breakoutRoomManagers) {
      try {
        yield fork(exchangeEncryptedNameSaga, manager);
      } catch (error) {
        Sentry.captureException(error);
        logger
          .remote({ system: true, capture: 'e2ee' })
          .error(`Name exchange failed with the userId=${manager.id}`, error);
      }
    }
  }

  const roomLastId: string = yield select(selectRoomLastId);

  // handle only on moving between rooms
  const movingBetweenRooms = roomLastId && roomId !== roomLastId;

  // auto zoom-to-fit only frames the board on a fresh join, not on a breakout move
  board.allowAutoFitOnMount = !movingBetweenRooms;

  if (!movingBetweenRooms) {
    // ensure we close the splash screen in other cases
    const showSplash: boolean = yield select(selectShowBreakoutsSplash);
    if (showSplash) {
      yield put(splashScreenCloseRequested());
    }
    return;
  }

  if (e2eeEnabled) {
    // regenerate keys for a new room
    yield call(E2EEManager.generateKeys, {
      ellipticCurveKeys: false,
    });
  }

  // @FIXME temporary fix until we refactor the media state authority
  // Clamp preserved media states to the target room's permissions.
  // prepareRoomMovementSaga preserves media states from the source room,
  // but the target room may have stricter permissions (e.g. audio-only).
  const canBroadcastVideo: boolean = yield call(hasPermissionsSaga, PermissionTypes.broadcastVideo);
  if (!canBroadcastVideo) {
    yield call(RTCClient.publishingFeed.disableMedia, 'video');
    yield put(virtualBackgroundCleared());
  }

  const canBroadcastAudio: boolean = yield call(hasPermissionsSaga, PermissionTypes.broadcastAudio);
  if (!canBroadcastAudio) {
    yield call(RTCClient.publishingFeed.disableMedia, 'audio');
  }

  yield call(onRoomJoinConfigSaga, action.payload);

  const showSplash: boolean = yield select(selectShowBreakoutsSplash);
  if (showSplash) {
    yield put(splashScreenCloseRequested());
  }
}
